import { nullifyValues, extractLegacyConfig } from './helpers';
import {
  Integration,
  IntegrationState,
} from '@callrail/inti/src/inti/proto/integration';

/**
 * By default our integration endpoints return a standardized Protobuf
 * response. However until the frontend can be updated to parse this response,
 * we need to provide it with the legacy response it is has historically
 * expected from the CallRail app.
 *
 * The entire purpose of this class is to transform a `Integration` protobuf object
 * into the same format generated by the CallRail application's
 * `integrations#show` endpoint
 *
 * See: https://github.com/callrail/callrail/blob/master/app/controllers/integrations_controller.rb#L74
 *
 * This same format is used to initialize the `IntegrationState` data model on
 * the looky frontend
 *
 * See: https://github.com/callrail/looky/blob/main/libs/looky/core/src/services/integration/integration-state.model.ts
 */

/**
 * Transforms an `inti.proto.Integration` protobuf object into a custom format
 * to match the legacy representation the frontend UI expects
 *
 * This data structure tries to match the response generated by the
 * `integrations#show` endpoint inside the CallRail monolith. Once all
 * integrations are migrated to `Inti`, that endpoint and this legacy functionality
 * can be deprecated.
 *
 * @see https://github.com/callrail/callrail/blob/master/app/controllers/integrations_controller.rb#L74
 * @param {Integration} an integration object, as defined in Protobuf (inti.proto.Integration)
 * @return {} Custom formatted object
 */
export function legacyTransformIntegration(integration: Integration) {
  return nullifyValues({
    config: extractLegacyConfig(integration),
    created_at: integration.createdAt,
    disabled: integration.state === IntegrationState.DISABLED,
    fail_count: integration.failCount,
    global: false,
    id: integration.uuid,
    last_modified_at: integration.lastModifiedAt,
    last_modifier_anonymous: null, // Not returned by `Inti` - default this to `null`
    last_modifier_id: integration.lastModifierMid,
    last_success_at: integration.lastSuccessAt,
    oauth_id: null,
    owner_id: integration.ownerMid,
    owner_type: integration.ownerType,
    state: IntegrationState[integration.state]?.toLowerCase(),
    state_changed_at: integration.stateChangedAt,
    updated_at: integration.updatedAt,
    error_message: integration.errorMessage,

    /**
     * `Integration` contains only data about the integration itself, but this
     * response requires us to include some class metadata and class-level
     * information.
     *
     * The frontend should already have this information cached from a previous
     * query, so to avoid making another redundant query ourselves we default
     * these to `null` and let the frontend fill them in
     */
    class: {},
    owner_name: null,
    site_name: null,
    white_label: null,
  });
}
